import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

/**
 * NVIDIA Rubin (VR200-class) GPU package study.
 *
 * Geometry is matched to NVIDIA's published package renders and to the
 * publicly announced architecture:
 *   - one organic substrate carrying a CoWoS-L class silicon interposer
 *   - two reticle-scale compute dies, mirrored about the package centreline,
 *     joined by a high-bandwidth die-to-die link and presented as one GPU
 *   - eight HBM4 stacks (four per die, two per die edge) = 288 GB @ ~13 TB/s
 *   - HBM PHY along the die edges facing the stacks, NVLink 6 PHY on the
 *     outer edges, L2/crossbar band through the die middle
 *
 * Announced figures used in the readout: 288 GB HBM4, ~13 TB/s, 50 PFLOPS
 * NVFP4 per package, NVLink 6 at 3.6 TB/s per GPU.
 *
 * Not public, therefore illustrative: SM/GPC counts, block sizes inside the
 * compute region, and the exact metal pattern. Those are drawn to be
 * structurally plausible, not to claim a real floorplan.
 */

// ---------------------------------------------------------------------------
// Package dimensions (world units, derived from NVIDIA's package render)
// ---------------------------------------------------------------------------
const PKG_W = 11.2;
const PKG_H = 9.56;
const FRAME_INNER_W = 9.52;
const FRAME_INNER_H = 8.2;

const DIE_W = 3.84;
const DIE_H = 3.8;
const SEAM = 0.05;
const DIE_CX = (DIE_W + SEAM) / 2;
const SILICON_HALF_W = DIE_CX + DIE_W / 2;

// GPC-scale blocks, expressed in die-local coordinates (outer edge -> seam).
const GPC_COLUMNS = [-0.83, 0.83];
const GPC_ROWS = [-0.79, 0.79];
const GPC_W = 1.5;
const GPC_H = 1.33;
const GPC_TILE_COLUMNS = 4;
const GPC_TILE_ROWS = 3;

const HBM_W = 1.46;
const HBM_H = 1.28;
const HBM_X = [-2.36, -0.84, 0.84, 2.36];
const HBM_Y = [-2.62, 2.62];

const Z_SUB_TOP = 0;
const Z_INTERPOSER = 0.16;
// Keep the textured silicon surface clear of the backing's top face. The
// backing ends at z=0.44; making these coplanar causes moving black depth
// patches (z-fighting) as the package tilts.
const Z_DIE_TOP = 0.452;
const Z_FLOORPLAN = 0.455;
const Z_RING = 0.54;
const Z_HBM_TOP = 0.52;

// ---------------------------------------------------------------------------
// Animation timeline — one dataflow cycle, then a stack-up teardown
// ---------------------------------------------------------------------------
const STAGES = [
  {
    id: 'fetch',
    end: 3.4,
    title: 'HBM4 fetch',
    detail: '8 stacks · 288 GB · ~13 TB/s into the HBM PHY',
  },
  {
    id: 'compute',
    end: 7.8,
    title: 'SM arrays · NVFP4',
    detail: '50 PFLOPS FP4 per package, swept die-edge to centreline',
  },
  {
    id: 'd2d',
    end: 9.8,
    title: 'Die-to-die exchange',
    detail: 'Twin reticle-scale dies addressed as a single GPU',
  },
  {
    id: 'nvlink',
    end: 12.2,
    title: 'NVLink 6 egress',
    detail: '3.6 TB/s per GPU out to the NVL144 domain',
  },
  {
    id: 'write',
    end: 14.6,
    title: 'HBM4 writeback',
    detail: 'Results retired to the stacks, cycle repeats',
  },
  {
    id: 'stackup',
    end: 19.6,
    title: 'Package stack-up',
    detail: 'Substrate · CoWoS-L interposer · 2 dies + 8 HBM4',
  },
];
const LOOP = STAGES.at(-1).end;
const INTRO_DURATION = 2.6;
const INTRO_HOLD = 0.35;
const INTRO_YAW = THREE.MathUtils.degToRad(25);
const INTRO_PITCH = THREE.MathUtils.degToRad(5);

const HOT = new THREE.Color(0xc9ff8a);
const MEMORY_COLOR = new THREE.Color(0x46e2d2);
const D2D_COLOR = new THREE.Color(0xffc46a);
const LINK_COLOR = new THREE.Color(0xb6ff7a);

export function createGpuScene({ panel, canvas, onStage }) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  });

  renderer.setClearColor(0x080909, 0);

  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.98;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(29, 1, 0.1, 120);
  const packageRoot = new THREE.Group();
  scene.add(packageRoot);

  addLights(scene);
  const parts = buildPackage(packageRoot, renderer, panel);

  const pointerTarget = new THREE.Vector2();
  const pointerCurrent = new THREE.Vector2();
  const clock = new THREE.Clock();
  let baseDistance = 24;
  let panelWidth = 1;
  let panelHeight = 1;
  let animationHandle = 0;
  let isVisible = true;
  let isDocumentVisible = !document.hidden;
  let disposed = false;
  let lastStageId = '';
  let introStart = null;

  function resize() {
    const rect = panel.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    const compact = width < 720;
    panelWidth = width;
    panelHeight = height;

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, compact ? 1 : 1.5));
    renderer.setSize(width, height, false);
    camera.aspect = width / height;

    const halfFov = THREE.MathUtils.degToRad(camera.fov * 0.5);
    const horizontalFit = 12.2 / (2 * Math.tan(halfFov) * camera.aspect);
    const verticalFit = 10.4 / (2 * Math.tan(halfFov));
    baseDistance = Math.max(horizontalFit, verticalFit) * 1.06;
    camera.updateProjectionMatrix();
    applyFrame(clock.getElapsedTime());
    renderer.render(scene, camera);
    canvas.classList.add('is-ready');
    panel.classList.add('is-ready');
  }

  function introTime(elapsed) {
    if (reducedMotion) return INTRO_DURATION;
    if (introStart === null) return 0;
    return elapsed - introStart;
  }

  /** 1 = exploded + angled intro pose, 0 = assembled resting pose. */
  function introBlend(elapsed) {
    const t = introTime(elapsed);
    if (t >= INTRO_DURATION) return 0;
    if (t <= INTRO_HOLD) return 1;
    return 1 - easeInOut((t - INTRO_HOLD) / (INTRO_DURATION - INTRO_HOLD));
  }

  function introFinished(elapsed) {
    return introTime(elapsed) >= INTRO_DURATION;
  }

  function applyFrame(elapsed) {
    const blend = introBlend(elapsed);
    const settled = Math.max(0, introTime(elapsed) - INTRO_DURATION);
    const stage = { id: 'compute', progress: (settled % 6) / 6 };

    if (typeof onStage === 'function') {
      const title = blend > 0.25 ? 'Package stack-up' : 'SM arrays · NVFP4';
      const detail = blend > 0.25
        ? 'Substrate · CoWoS-L interposer · 2 dies + 8 HBM4'
        : '50 PFLOPS FP4 per package';
      if (title !== lastStageId) {
        lastStageId = title;
        onStage({ title, detail });
      }
    }

    const pointerX = introFinished(elapsed) ? pointerCurrent.x : 0;
    const pointerY = introFinished(elapsed) ? pointerCurrent.y : 0;
    const restX = -0.17 + pointerX;
    const restY = 0.02 + pointerY + Math.sin(elapsed * 0.13) * 0.012;
    packageRoot.rotation.x = restX - blend * INTRO_PITCH;
    packageRoot.rotation.y = restY + blend * INTRO_YAW;
    packageRoot.position.set(0, 0.05 + Math.sin(elapsed * 0.4) * 0.016, 0);

    camera.position.set(0, 0.1 + blend * 1.1, baseDistance * (1 - blend * 0.06));
    camera.lookAt(0, 0, 0);
    camera.updateMatrixWorld();

    updateStackUp(parts, blend);
    updateLabels(parts, camera, blend, panelWidth, panelHeight);
    updateSmField(parts.smField, stage, elapsed, blend);
  }

  function renderFrame() {
    if (disposed || !isVisible || !isDocumentVisible) {
      animationHandle = 0;
      return;
    }
    const elapsed = clock.getElapsedTime();
    if (introStart === null) introStart = elapsed;
    if (introFinished(elapsed)) {
      pointerCurrent.lerp(pointerTarget, 0.06);
    } else {
      pointerTarget.set(0, 0);
      pointerCurrent.set(0, 0);
    }
    applyFrame(elapsed);
    renderer.render(scene, camera);
    animationHandle = requestAnimationFrame(renderFrame);
  }

  function start() {
    if (reducedMotion) return;
    if (!animationHandle && isVisible && isDocumentVisible && !disposed) {
      animationHandle = requestAnimationFrame(renderFrame);
    }
  }

  function stop() {
    if (animationHandle) cancelAnimationFrame(animationHandle);
    animationHandle = 0;
  }

  function onPointerMove(event) {
    if (reducedMotion) return;
    const elapsed = clock.getElapsedTime();
    if (introStart === null || !introFinished(elapsed)) return;
    const rect = panel.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    pointerTarget.set(
      THREE.MathUtils.clamp(-y * 0.06, -0.06, 0.06),
      THREE.MathUtils.clamp(x * 0.085, -0.085, 0.085),
    );
  }

  function onPointerLeave() {
    pointerTarget.set(0, 0);
  }

  function onVisibilityChange() {
    isDocumentVisible = !document.hidden;
    if (isDocumentVisible) start();
    else stop();
  }

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(panel);
  const intersectionObserver = new IntersectionObserver(
    ([entry]) => {
      isVisible = Boolean(entry?.isIntersecting);
      if (isVisible) start();
      else stop();
    },
    { rootMargin: '160px' },
  );
  intersectionObserver.observe(panel);

  panel.addEventListener('pointermove', onPointerMove, { passive: true });
  panel.addEventListener('pointerleave', onPointerLeave, { passive: true });
  document.addEventListener('visibilitychange', onVisibilityChange);

  resize();
  start();

  return () => {
    disposed = true;
    stop();
    canvas.classList.remove('is-ready');
    panel.classList.remove('is-ready');
    resizeObserver.disconnect();
    intersectionObserver.disconnect();
    panel.removeEventListener('pointermove', onPointerMove);
    panel.removeEventListener('pointerleave', onPointerLeave);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    parts.textures.forEach((texture) => texture.dispose());
    scene.traverse((object) => {
      object.geometry?.dispose();
      if (Array.isArray(object.material)) object.material.forEach((m) => m.dispose());
      else object.material?.dispose();
    });
    renderer.dispose();
  };
}

// ---------------------------------------------------------------------------
// Timeline helpers
// ---------------------------------------------------------------------------
function stageAt(time) {
  let start = 0;
  for (const stage of STAGES) {
    if (time < stage.end) {
      return { ...stage, progress: (time - start) / (stage.end - start) };
    }
    start = stage.end;
  }
  const last = STAGES.at(-1);
  return { ...last, progress: 1 };
}

function explodeAmount(progress) {
  // rise, hold, settle inside the stack-up stage
  if (progress < 0.3) return easeInOut(progress / 0.3);
  if (progress < 0.66) return 1;
  return 1 - easeInOut((progress - 0.66) / 0.34);
}

function easeInOut(x) {
  const t = THREE.MathUtils.clamp(x, 0, 1);
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

function ramp(x, edge0, edge1) {
  return THREE.MathUtils.smoothstep(THREE.MathUtils.clamp(x, 0, 1), edge0, edge1);
}

/** Wavefront head as a normalised distance-from-seam value (1 = outer edge). */
function computeHead(stage) {
  if (stage.id === 'compute') return 1 - stage.progress * 1.12;
  if (stage.id === 'write') return stage.progress * 1.12;
  return -1;
}

// ---------------------------------------------------------------------------
// Scene construction
// ---------------------------------------------------------------------------
function addLights(scene) {
  scene.add(new THREE.HemisphereLight(0xfff6df, 0x050605, 0.7));

  const key = new THREE.DirectionalLight(0xffe9bd, 3.1);
  key.position.set(-5, 8, 12);
  scene.add(key);

  const fill = new THREE.DirectionalLight(0xc7d2cb, 0.8);
  fill.position.set(7, 0, 9);
  scene.add(fill);

  const rim = new THREE.PointLight(0x9fd8c0, 3.2, 22);
  rim.position.set(2, -6, 4);
  scene.add(rim);
}

function roundedBox(width, height, depth, radius, material) {
  return new THREE.Mesh(new RoundedBoxGeometry(width, height, depth, 3, radius), material);
}

function buildPackage(root, renderer, panel) {
  const textures = [];

  const gSubstrate = new THREE.Group();
  const gInterposer = new THREE.Group();
  const gSilicon = new THREE.Group();
  const gHbm = new THREE.Group();
  const gFlow = new THREE.Group();
  gFlow.visible = false;
  root.add(gSubstrate, gInterposer, gSilicon, gHbm, gFlow);

  buildSubstrate(gSubstrate);
  buildInterposer(gInterposer);

  const dieTextures = createDieTextures(renderer);
  textures.push(...dieTextures);
  const floorplan = buildComputeDies(gSilicon, dieTextures);
  const hbm = buildMemoryStacks(gHbm);

  const glow = createGlowTexture();
  textures.push(glow);
  const emitters = buildEmitters(gFlow, glow, floorplan, hbm);
  const packets = buildPackets(gFlow);
  const sweepMaterial = buildSweepLayer(gFlow);
  const labels = buildStackLabels([gSubstrate, gInterposer, gSilicon, gHbm], panel);

  return {
    groups: { gSubstrate, gInterposer, gSilicon, gHbm, gFlow },
    smField: floorplan.smField,
    strips: floorplan.strips,
    hbm,
    emitters,
    packets,
    sweepMaterial,
    labels,
    textures,
  };
}

function buildSubstrate(group) {
  const substrateMaterial = new THREE.MeshStandardMaterial({
    color: 0x1c1a15,
    metalness: 0.55,
    roughness: 0.45,
  });
  const champagne = new THREE.MeshPhysicalMaterial({
    color: 0xb0a077,
    metalness: 0.9,
    roughness: 0.3,
    clearcoat: 0.28,
    clearcoatRoughness: 0.3,
  });
  const blackCeramic = new THREE.MeshStandardMaterial({
    color: 0x080a09,
    metalness: 0.42,
    roughness: 0.52,
  });

  const substrate = roundedBox(PKG_W, PKG_H, 0.62, 0.2, substrateMaterial);
  substrate.position.z = Z_SUB_TOP - 0.31;
  group.add(substrate);

  // Champagne stiffener ring around the die/memory cavity.
  const railX = (PKG_W - FRAME_INNER_W) / 2;
  const railY = (PKG_H - FRAME_INNER_H) / 2;
  const rails = [
    [0, (PKG_H - railY) / 2, PKG_W, railY],
    [0, -(PKG_H - railY) / 2, PKG_W, railY],
    [-(PKG_W - railX) / 2, 0, railX, PKG_H - 2 * railY],
    [(PKG_W - railX) / 2, 0, railX, PKG_H - 2 * railY],
  ];
  for (const [x, y, w, h] of rails) {
    const rail = roundedBox(w, h, 0.16, 0.05, champagne);
    rail.position.set(x, y, Z_SUB_TOP + 0.08);
    group.add(rail);
  }

  const cavityFloor = roundedBox(FRAME_INNER_W, FRAME_INNER_H, 0.08, 0.04, blackCeramic);
  cavityFloor.position.z = Z_SUB_TOP + 0.04;
  group.add(cavityFloor);

  buildSubstrateRouting(group);
  buildLandPatterns(group);
}

function buildSubstrateRouting(group) {
  const points = [];
  for (let index = 0; index < 26; index += 1) {
    const x = -4.5 + index * 0.36;
    points.push(x, -3.85, 0.085, x, 3.85, 0.085);
  }
  for (let index = 0; index < 18; index += 1) {
    const y = -3.75 + index * 0.44;
    points.push(-4.62, y, 0.085, 4.62, y, 0.085);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
  group.add(
    new THREE.LineSegments(
      geometry,
      new THREE.LineBasicMaterial({ color: 0x5c6455, transparent: true, opacity: 0.14 }),
    ),
  );
}

function buildLandPatterns(group) {
  const padMaterial = new THREE.MeshStandardMaterial({
    color: 0xcbbb8a,
    metalness: 0.95,
    roughness: 0.2,
  });

  const horizontal = new THREE.InstancedMesh(new THREE.BoxGeometry(0.1, 0.055, 0.05), padMaterial, 108);
  const vertical = new THREE.InstancedMesh(new THREE.BoxGeometry(0.055, 0.1, 0.05), padMaterial, 60);
  const dummy = new THREE.Object3D();

  let index = 0;
  for (const y of [-3.78, 3.78]) {
    for (let column = 0; column < 54; column += 1) {
      dummy.position.set(-4.42 + column * 0.1668, y, 0.11);
      dummy.updateMatrix();
      horizontal.setMatrixAt(index, dummy.matrix);
      index += 1;
    }
  }

  index = 0;
  for (const x of [-4.62, 4.62]) {
    for (let row = 0; row < 30; row += 1) {
      dummy.position.set(x, -3.19 + row * 0.22, 0.11);
      dummy.updateMatrix();
      vertical.setMatrixAt(index, dummy.matrix);
      index += 1;
    }
  }
  horizontal.instanceMatrix.needsUpdate = true;
  vertical.instanceMatrix.needsUpdate = true;
  group.add(horizontal, vertical);

  // Corner fiducials / alignment vias.
  const fiducial = new THREE.MeshStandardMaterial({ color: 0xcab98f, metalness: 0.9, roughness: 0.24 });
  for (const x of [-4.66, 4.66]) {
    for (const y of [-3.98, 3.98]) {
      const via = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.04, 16), fiducial);
      via.rotation.x = Math.PI / 2;
      via.position.set(x, y, 0.12);
      group.add(via);
    }
  }
}

function buildInterposer(group) {
  const interposer = roundedBox(
    SILICON_HALF_W * 2 + 0.32,
    Math.abs(HBM_Y[0]) * 2 + HBM_H + 0.28,
    0.16,
    0.03,
    new THREE.MeshStandardMaterial({
      color: 0x14171a,
      metalness: 0.62,
      roughness: 0.36,
    }),
  );
  interposer.position.z = Z_INTERPOSER - 0.08;
  group.add(interposer);

  // Micro-bump field on the interposer top — only visible in the stack-up view.
  const bumpMaterial = new THREE.MeshStandardMaterial({
    color: 0x8f8360,
    metalness: 0.9,
    roughness: 0.3,
  });
  const columns = 74;
  const rows = 44;
  const bumps = new THREE.InstancedMesh(
    new THREE.SphereGeometry(0.022, 6, 5),
    bumpMaterial,
    columns * rows,
  );
  const dummy = new THREE.Object3D();
  let index = 0;
  for (let column = 0; column < columns; column += 1) {
    for (let row = 0; row < rows; row += 1) {
      dummy.position.set(-3.94 + column * 0.108, -3.15 + row * 0.147, Z_INTERPOSER + 0.01);
      dummy.updateMatrix();
      bumps.setMatrixAt(index, dummy.matrix);
      index += 1;
    }
  }
  bumps.instanceMatrix.needsUpdate = true;
  group.add(bumps);
}

// ---------------------------------------------------------------------------
// Compute dies
// ---------------------------------------------------------------------------
function buildComputeDies(group, dieTextures) {
  const strips = {
    hbmPhy: new THREE.MeshStandardMaterial({
      color: 0x1c2b26,
      metalness: 0.16,
      roughness: 0.82,
      emissive: MEMORY_COLOR.clone(),
      emissiveIntensity: 0.08,
    }),
    d2d: new THREE.MeshStandardMaterial({
      color: 0x2a2317,
      metalness: 0.16,
      roughness: 0.82,
      emissive: D2D_COLOR.clone(),
      emissiveIntensity: 0.08,
    }),
    nvlink: new THREE.MeshStandardMaterial({
      color: 0x1f2917,
      metalness: 0.16,
      roughness: 0.82,
      emissive: LINK_COLOR.clone(),
      emissiveIntensity: 0.08,
    }),
    l2: new THREE.MeshStandardMaterial({
      color: 0x3a3222,
      metalness: 0.2,
      roughness: 0.78,
      emissive: new THREE.Color(0x9fe07a),
      emissiveIntensity: 0.03,
    }),
  };

  const dieBase = new THREE.MeshStandardMaterial({
    color: 0x090c0a,
    metalness: 0.6,
    roughness: 0.36,
  });

  for (let side = 0; side < 2; side += 1) {
    const sign = side === 0 ? -1 : 1;
    const cx = sign * DIE_CX;

    const backing = roundedBox(DIE_W, DIE_H, 0.28, 0.02, dieBase);
    backing.position.set(cx, 0, Z_INTERPOSER + 0.14);
    group.add(backing);

    const surface = new THREE.Mesh(
      new THREE.PlaneGeometry(DIE_W - 0.02, DIE_H - 0.02),
      new THREE.MeshStandardMaterial({
        map: dieTextures[side],
        metalness: 0.3,
        roughness: 0.56,
        emissive: 0x0b1712,
        emissiveIntensity: 0.1,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -2,
      }),
    );
    surface.position.set(cx, 0, Z_DIE_TOP);
    group.add(surface);

    // HBM4 PHY: one strip per adjacent stack, at the top and bottom die edges.
    for (const y of HBM_Y) {
      for (const x of HBM_X) {
        if (Math.sign(x) !== sign) continue;
        const phy = roundedBox(HBM_W - 0.06, 0.3, 0.035, 0.012, strips.hbmPhy);
        phy.position.set(x, y > 0 ? DIE_H / 2 - 0.19 : -DIE_H / 2 + 0.19, Z_FLOORPLAN);
        group.add(phy);
      }
    }

    // NVLink 6 PHY on the outer die edge.
    const link = roundedBox(0.26, DIE_H - 0.9, 0.035, 0.012, strips.nvlink);
    link.position.set(cx + sign * (DIE_W / 2 - 0.17), 0, Z_FLOORPLAN);
    group.add(link);

    // Die-to-die interface facing the package centreline.
    const d2d = roundedBox(0.17, DIE_H - 0.5, 0.035, 0.01, strips.d2d);
    d2d.position.set(cx - sign * (DIE_W / 2 - 0.11), 0, Z_FLOORPLAN);
    group.add(d2d);

    // L2 / crossbar band through the die middle.
    const l2 = roundedBox(DIE_W - 0.7, 0.11, 0.03, 0.008, strips.l2);
    l2.position.set(cx, 0, Z_FLOORPLAN);
    group.add(l2);
  }

  const smField = buildSmArrays(group, strips);
  return { strips, smField };
}

/**
 * Four GPC-scale blocks per die, each a grid of SM indicators. Counts are
 * illustrative — NVIDIA has not published a Rubin SM/GPC breakdown.
 */
function buildSmArrays(group, strips) {
  // The GPC block outlines are drawn into the die texture. Only the metal
  // indicators sit above it; there are no dark boxes intersecting the rings.
  const ringGeometry = new THREE.TorusGeometry(0.107, 0.027, 6, 20);
  const dotGeometry = new THREE.CircleGeometry(0.055, 14);

  const positions = [];

  for (let side = 0; side < 2; side += 1) {
    const sign = side === 0 ? -1 : 1;
    const cx = sign * DIE_CX;
    for (const columnOffset of GPC_COLUMNS) {
      for (const rowOffset of GPC_ROWS) {
        const bx = cx + sign * columnOffset;
        const by = rowOffset;
        for (let row = 0; row < GPC_TILE_ROWS; row += 1) {
          for (let column = 0; column < GPC_TILE_COLUMNS; column += 1) {
            positions.push({
              x: bx - 0.51 + column * 0.34,
              y: by - 0.4 + row * 0.4,
            });
          }
        }
      }
    }
  }

  // Fabric channels between the GPC blocks.
  for (let side = 0; side < 2; side += 1) {
    const sign = side === 0 ? -1 : 1;
    const cx = sign * DIE_CX;
    const channel = roundedBox(0.09, DIE_H - 1.0, 0.028, 0.008, strips.l2);
    channel.position.set(cx, 0, Z_FLOORPLAN - 0.002);
    group.add(channel);
  }

  const count = positions.length;
  const rings = new THREE.InstancedMesh(
    ringGeometry,
    new THREE.MeshBasicMaterial({ vertexColors: true }),
    count,
  );
  const dots = new THREE.InstancedMesh(
    dotGeometry,
    new THREE.MeshBasicMaterial({ vertexColors: true }),
    count,
  );

  const dummy = new THREE.Object3D();
  const baseColors = [];
  const scratch = new THREE.Color();

  positions.forEach((position, index) => {
    dummy.position.set(position.x, position.y, Z_RING);
    dummy.updateMatrix();
    rings.setMatrixAt(index, dummy.matrix);
    dummy.position.z = Z_RING + 0.005;
    dummy.updateMatrix();
    dots.setMatrixAt(index, dummy.matrix);

    position.seamDist = Math.min(1, Math.abs(position.x) / SILICON_HALF_W);
    position.edgeY = Math.min(1, Math.abs(position.y) / (DIE_H / 2));
    position.jitter = (Math.sin(index * 12.9898) * 43758.5453) % 1;

    const base = irisColor(position.seamDist, position.y / (DIE_H / 2));
    baseColors.push(base);
    rings.setColorAt(index, ringTint(scratch.copy(base)));
    dots.setColorAt(index, scratch.copy(base).multiplyScalar(0.82));
  });

  rings.instanceMatrix.needsUpdate = true;
  dots.instanceMatrix.needsUpdate = true;
  group.add(rings, dots);

  return { rings, dots, positions, baseColors, count, scratch: new THREE.Color() };
}

const RING_HIGHLIGHT = new THREE.Color(0xfff2c8);

/** SM tiles read as bright metal rings over the dark block fill. */
function ringTint(color) {
  return color.lerp(RING_HIGHLIGHT, 0.34).multiplyScalar(1.35);
}

/** Iridescent oxide colouring, mirrored across the package centreline. */
function irisColor(seamDist, verticalRatio) {
  const stops = [
    [0, new THREE.Color(0x4bb9b2)],
    [0.24, new THREE.Color(0xc59a53)],
    [0.48, new THREE.Color(0xb98a4b)],
    [0.72, new THREE.Color(0x7fa762)],
    [1, new THREE.Color(0x49c3c0)],
  ];
  let color = stops.at(-1)[1].clone();
  for (let index = 0; index < stops.length - 1; index += 1) {
    const [startAt, startColor] = stops[index];
    const [endAt, endColor] = stops[index + 1];
    if (seamDist <= endAt) {
      const amount = THREE.MathUtils.clamp((seamDist - startAt) / (endAt - startAt), 0, 1);
      color = startColor.clone().lerp(endColor, amount);
      break;
    }
  }
  const centreMetal = new THREE.Color(0xa67b45);
  return color.lerp(centreMetal, Math.max(0, 0.22 - Math.abs(verticalRatio) * 0.22));
}

// ---------------------------------------------------------------------------
// HBM4 stacks
// ---------------------------------------------------------------------------
function buildMemoryStacks(group) {
  const dramMaterial = new THREE.MeshStandardMaterial({
    color: 0x1a1913,
    metalness: 0.7,
    roughness: 0.34,
  });
  const capMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xc9b98f,
    metalness: 0.66,
    roughness: 0.28,
    clearcoat: 0.28,
    clearcoatRoughness: 0.26,
    emissive: MEMORY_COLOR.clone(),
    emissiveIntensity: 0,
  });

  const stacks = [];
  for (const y of HBM_Y) {
    for (const x of HBM_X) {
      const stack = new THREE.Group();
      stack.position.set(x, y, 0);
      group.add(stack);

      // Base logic die plus the DRAM layers above it (HBM4 stacks are
      // 12-high at 36 GB per stack for a 288 GB package).
      const baseDie = roundedBox(HBM_W, HBM_H, 0.1, 0.02, dramMaterial);
      baseDie.position.z = Z_INTERPOSER + 0.05;
      stack.add(baseDie);

      const layers = 12;
      const layerHeight = 0.019;
      for (let layer = 0; layer < layers; layer += 1) {
        const dram = new THREE.Mesh(
          new THREE.BoxGeometry(HBM_W - 0.05, HBM_H - 0.05, layerHeight),
          dramMaterial,
        );
        dram.position.z = Z_INTERPOSER + 0.1 + layerHeight * (layer + 0.5);
        stack.add(dram);
      }

      const cap = roundedBox(HBM_W - 0.02, HBM_H - 0.02, 0.09, 0.015, capMaterial);
      cap.position.z = Z_HBM_TOP - 0.045;
      stack.add(cap);

      stacks.push({ group: stack, x, y, cap });
    }
  }
  return { stacks, capMaterial };
}

// ---------------------------------------------------------------------------
// Flow layer: glow emitters, packets, compute sweep
// ---------------------------------------------------------------------------
function createGlowTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext('2d');
  const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.35, 'rgba(255,255,255,0.42)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(canvas);
}

function glowPlane(width, height, color, texture) {
  return new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    new THREE.MeshBasicMaterial({
      map: texture,
      color,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
}

function buildEmitters(group, glowTexture) {
  const phy = [];
  const seam = [];
  const link = [];
  const memory = [];

  for (const y of HBM_Y) {
    for (const x of HBM_X) {
      const strip = glowPlane(HBM_W + 0.5, 1.0, MEMORY_COLOR, glowTexture);
      strip.position.set(x, y > 0 ? DIE_H / 2 - 0.19 : -DIE_H / 2 + 0.19, Z_RING + 0.02);
      group.add(strip);
      phy.push(strip.material);

      const stackGlow = glowPlane(HBM_W + 0.8, HBM_H + 0.8, MEMORY_COLOR, glowTexture);
      stackGlow.position.set(x, y, Z_HBM_TOP + 0.02);
      group.add(stackGlow);
      memory.push(stackGlow.material);
    }
  }

  const seamGlow = glowPlane(1.6, DIE_H + 0.6, D2D_COLOR, glowTexture);
  seamGlow.position.set(0, 0, Z_RING + 0.02);
  group.add(seamGlow);
  seam.push(seamGlow.material);

  for (const sign of [-1, 1]) {
    const linkGlow = glowPlane(1.5, DIE_H, LINK_COLOR, glowTexture);
    linkGlow.position.set(sign * SILICON_HALF_W, 0, Z_RING + 0.02);
    group.add(linkGlow);
    link.push(linkGlow.material);
  }

  return { phy, seam, link, memory };
}

/**
 * Packet lanes. Memory lanes are reused in both directions (fetch and
 * writeback); the die-to-die and NVLink lanes only run in their own stage.
 */
function buildPackets(group) {
  const lanes = [];
  const PER_LANE = 2;

  const push = (lane) => {
    for (let copy = 0; copy < PER_LANE; copy += 1) {
      lanes.push({ ...lane, offset: (lane.offset + copy / PER_LANE) % 1 });
    }
  };

  // HBM <-> die lanes, reused in both directions (fetch and writeback).
  for (const y of HBM_Y) {
    for (const x of HBM_X) {
      for (let lane = 0; lane < 4; lane += 1) {
        const lx = x - 0.45 + lane * 0.3;
        const sign = Math.sign(y);
        push({
          route: 'memory',
          axis: 'y',
          from: new THREE.Vector3(lx, sign * 2.1, Z_HBM_TOP + 0.05),
          to: new THREE.Vector3(lx, sign * 0.45, Z_RING + 0.05),
          offset: lane * 0.13,
          color: MEMORY_COLOR,
        });
      }
    }
  }

  // Die-to-die traffic, running both ways across the centreline.
  for (let lane = 0; lane < 7; lane += 1) {
    const y = -1.35 + lane * 0.45;
    push({
      route: 'd2d',
      axis: 'x',
      from: new THREE.Vector3(-1.7, y, Z_RING + 0.04),
      to: new THREE.Vector3(1.7, y, Z_RING + 0.04),
      offset: lane * 0.1,
      color: D2D_COLOR,
    });
    push({
      route: 'd2d',
      axis: 'x',
      from: new THREE.Vector3(1.7, -y, Z_RING + 0.04),
      to: new THREE.Vector3(-1.7, -y, Z_RING + 0.04),
      offset: lane * 0.1 + 0.05,
      color: D2D_COLOR,
    });
  }

  // NVLink egress out over the substrate to the package edge.
  for (const sign of [-1, 1]) {
    for (let lane = 0; lane < 6; lane += 1) {
      const y = -1.3 + lane * 0.52;
      push({
        route: 'nvlink',
        axis: 'x',
        from: new THREE.Vector3(sign * (SILICON_HALF_W - 0.2), y, Z_RING + 0.04),
        to: new THREE.Vector3(sign * (PKG_W / 2 + 0.5), y, Z_SUB_TOP + 0.2),
        offset: lane * 0.12,
        color: LINK_COLOR,
      });
    }
  }

  const mesh = new THREE.InstancedMesh(
    new THREE.BoxGeometry(0.1, 0.1, 0.05),
    new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
    lanes.length,
  );
  mesh.frustumCulled = false;
  lanes.forEach((lane, index) => mesh.setColorAt(index, lane.color));
  group.add(mesh);

  return { mesh, lanes, dummy: new THREE.Object3D(), scratch: new THREE.Color() };
}

function buildSweepLayer(group) {
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uHead: { value: -1 },
      uStrength: { value: 0 },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uTime;
      uniform float uHead;
      uniform float uStrength;
      varying vec2 vUv;

      void main() {
        // Distance from the package centreline, normalised to the die edge.
        float seamDist = abs(vUv.x * 2.0 - 1.0);
        float band = exp(-pow((seamDist - uHead) * 7.0, 2.0));
        float trail = smoothstep(uHead + 0.24, uHead - 0.05, seamDist) * 0.22;
        float lanes = pow(max(0.0, sin(vUv.y * 118.0 + uTime * 4.5)), 14.0);
        vec3 color = mix(vec3(0.28, 0.86, 0.66), vec3(0.74, 1.0, 0.46), vUv.y);
        float alpha = (band * (0.16 + lanes * 0.5) + trail * 0.35) * uStrength;
        gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
      }
    `,
  });
  const layer = new THREE.Mesh(new THREE.PlaneGeometry(SILICON_HALF_W * 2, DIE_H - 0.06), material);
  layer.position.set(0, 0, Z_RING + 0.015);
  group.add(layer);
  return material;
}

/**
 * Anchors for the HTML layer captions shown during the stack-up. Text is kept
 * in the DOM rather than in a sprite so it stays crisp at any canvas size.
 */
function buildStackLabels(groups, panel) {
  const keys = ['substrate', 'interposer', 'die', 'hbm'];
  const anchors = [
    new THREE.Vector3(6.4, -1.6, 0.1),
    new THREE.Vector3(6.4, -0.4, 0.1),
    new THREE.Vector3(6.4, 0.7, 0.35),
    new THREE.Vector3(6.4, 2.4, 0.5),
  ];

  return groups
    .map((group, index) => {
      const element = panel?.querySelector(`[data-layer="${keys[index]}"]`);
      if (!element) return null;
      return { group, element, anchor: anchors[index], projected: new THREE.Vector3() };
    })
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Die surface texture (drawn once, mirrored for the second die)
// ---------------------------------------------------------------------------
function createDieTextures(renderer) {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 1024;
  const context = canvas.getContext('2d');
  const random = seededRandom(90210);

  // u = 0 is the outer die edge, u = 1 is the package centreline.
  const gradient = context.createLinearGradient(0, canvas.height, canvas.width, 0);
  gradient.addColorStop(0, '#0f5f63');
  gradient.addColorStop(0.16, '#1f6742');
  gradient.addColorStop(0.36, '#7d6124');
  gradient.addColorStop(0.55, '#83512a');
  gradient.addColorStop(0.72, '#63454c');
  gradient.addColorStop(0.88, '#2c6a63');
  gradient.addColorStop(1, '#0f5f63');
  context.fillStyle = gradient;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const oxide = context.createLinearGradient(0, 0, canvas.width * 0.5, canvas.height);
  oxide.addColorStop(0, 'rgba(85,120,87,0)');
  oxide.addColorStop(0.55, 'rgba(164,119,54,0.28)');
  oxide.addColorStop(1, 'rgba(85,120,87,0)');
  context.fillStyle = oxide;
  context.fillRect(0, 0, canvas.width, canvas.height);

  // Standard-cell / SRAM texture.
  context.lineWidth = 1;
  for (let index = 0; index < 1400; index += 1) {
    const x = Math.floor(random() * canvas.width);
    const y = Math.floor(random() * canvas.height);
    const width = 4 + Math.floor(random() * 44);
    const height = 3 + Math.floor(random() * 18);
    context.strokeStyle = random() > 0.5 ? 'rgba(5,14,12,.36)' : 'rgba(230,224,180,.18)';
    context.strokeRect(x, y, width, height);
    if (random() > 0.72) {
      context.fillStyle = 'rgba(5,10,9,.2)';
      context.fillRect(x + 2, y + 2, Math.max(1, width - 4), Math.max(1, height - 4));
    }
  }

  // GPC block outlines, in the same die-local positions as the SM instances.
  const toU = (localX) => (localX + DIE_W / 2) / DIE_W;
  const toV = (localY) => 1 - (localY + DIE_H / 2) / DIE_H;
  const blockRects = [];
  for (const columnOffset of GPC_COLUMNS) {
    for (const rowOffset of GPC_ROWS) {
      blockRects.push([
        toU(columnOffset - GPC_W / 2) * canvas.width,
        toV(rowOffset + GPC_H / 2) * canvas.height,
        (GPC_W / DIE_W) * canvas.width,
        (GPC_H / DIE_H) * canvas.height,
      ]);
    }
  }

  // Everything outside the GPC blocks (fabric, cache, PHY) is darker metal;
  // the blocks keep the bright iridescent oxide.
  const channels = new Path2D();
  channels.rect(0, 0, canvas.width, canvas.height);
  for (const [x, y, width, height] of blockRects) channels.rect(x, y, width, height);
  context.fillStyle = 'rgba(6,11,9,0.62)';
  context.fill(channels, 'evenodd');

  for (const [x, y, width, height] of blockRects) {
    context.strokeStyle = 'rgba(236,228,178,0.34)';
    context.lineWidth = 3;
    context.strokeRect(x, y, width, height);
    context.lineWidth = 1;
    context.strokeStyle = 'rgba(6,12,10,0.28)';
    for (let inner = 1; inner < 5; inner += 1) {
      context.strokeRect(x + inner * 5, y + inner * 5, width - inner * 10, height - inner * 10);
    }
  }

  // Routing channels.
  context.globalAlpha = 0.26;
  for (let index = 0; index < 300; index += 1) {
    const startX = random() * canvas.width;
    const startY = random() * canvas.height;
    const dx = (random() - 0.5) * 160;
    const dy = (random() - 0.5) * 70;
    context.beginPath();
    context.moveTo(startX, startY);
    context.lineTo(startX + dx * 0.45, startY);
    context.lineTo(startX + dx * 0.45, startY + dy);
    context.lineTo(startX + dx, startY + dy);
    context.strokeStyle = random() > 0.5 ? '#dfe6c6' : '#07100d';
    context.stroke();
  }
  context.globalAlpha = 1;

  // Dense PHY hatching along the top and bottom edges (HBM4 side).
  context.globalAlpha = 0.4;
  context.strokeStyle = '#0a1410';
  for (const band of [0, canvas.height - 78]) {
    for (let x = 0; x < canvas.width; x += 5) {
      context.beginPath();
      context.moveTo(x, band);
      context.lineTo(x, band + 78);
      context.stroke();
    }
  }
  // NVLink PHY hatching on the outer edge.
  for (let y = 0; y < canvas.height; y += 5) {
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(70, y);
    context.stroke();
  }
  context.globalAlpha = 1;

  const vignette = context.createRadialGradient(512, 512, 80, 512, 512, 720);
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(0,0,0,.28)');
  context.fillStyle = vignette;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());

  // Left die: outer edge on the left, so the texture is used as drawn.
  const left = new THREE.CanvasTexture(canvas);
  left.colorSpace = THREE.SRGBColorSpace;
  left.anisotropy = anisotropy;

  // Right die: mirrored, matching the mirrored twin dies in the real package.
  const right = new THREE.CanvasTexture(canvas);
  right.colorSpace = THREE.SRGBColorSpace;
  right.anisotropy = anisotropy;
  right.wrapS = THREE.RepeatWrapping;
  right.repeat.x = -1;
  right.offset.x = 1;

  return [left, right];
}

function seededRandom(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

// ---------------------------------------------------------------------------
// Per-frame updates
// ---------------------------------------------------------------------------
function updateStackUp(parts, explode) {
  const { gInterposer, gSilicon, gHbm, gFlow } = parts.groups;
  gInterposer.position.z = explode * 0.95;
  gSilicon.position.z = explode * 2.15;
  gHbm.position.z = explode * 2.15;
  gFlow.position.z = explode * 2.15;
  gFlow.visible = explode < 0.35;

  for (const stack of parts.hbm.stacks) {
    stack.group.position.y = stack.y + Math.sign(stack.y) * explode * 0.72;
    stack.group.position.x = stack.x + Math.sign(stack.x) * explode * 0.24;
  }
}

/** Projects the layer captions onto the canvas while the package is apart. */
function updateLabels(parts, camera, explode, width, height) {
  const opacity = Math.max(0, explode * 1.8 - 0.8);
  for (const label of parts.labels) {
    if (opacity <= 0) {
      label.element.style.opacity = '0';
      continue;
    }
    label.projected.copy(label.anchor);
    label.group.localToWorld(label.projected);
    label.projected.project(camera);
    label.element.style.opacity = String(opacity);
    label.element.style.transform = `translate(${((label.projected.x * 0.5 + 0.5) * width).toFixed(1)}px, ${((-label.projected.y * 0.5 + 0.5) * height).toFixed(1)}px)`;
  }
}

function updateEmitters(parts, stage, elapsed, explode) {
  const flicker = 0.85 + 0.15 * Math.sin(elapsed * 9);
  const fade = 1 - explode;

  const memoryActive =
    (stage.id === 'fetch' ? ramp(stage.progress, 0, 0.25) * (1 - ramp(stage.progress, 0.8, 1) * 0.4) : 0) +
    (stage.id === 'write' ? ramp(stage.progress, 0.1, 0.4) : 0);
  const d2dActive = stage.id === 'd2d' ? Math.sin(Math.PI * stage.progress) : 0;
  const linkActive = stage.id === 'nvlink' ? Math.sin(Math.PI * stage.progress) : 0;
  const computeActive = stage.id === 'compute' ? Math.sin(Math.PI * stage.progress) : 0;

  for (const material of parts.emitters.phy) material.opacity = memoryActive * 0.18 * flicker * fade;
  for (const material of parts.emitters.memory) material.opacity = memoryActive * 0.09 * flicker * fade;
  for (const material of parts.emitters.seam) material.opacity = d2dActive * 0.6 * flicker * fade;
  for (const material of parts.emitters.link) material.opacity = linkActive * 0.5 * flicker * fade;

  parts.strips.hbmPhy.emissiveIntensity = 0.08 + memoryActive * 1.1 * flicker;
  parts.strips.d2d.emissiveIntensity = 0.08 + d2dActive * 3.2 * flicker;
  parts.strips.nvlink.emissiveIntensity = 0.08 + linkActive * 3.0 * flicker;
  parts.strips.l2.emissiveIntensity = 0.03 + computeActive * 1.0;
  parts.hbm.capMaterial.emissiveIntensity = memoryActive * 0.12 * flicker;
}

function updateSmField(field, stage, elapsed, explode) {
  const head = computeHead(stage);
  const { scratch } = field;
  const idle = 1 - explode;

  for (let index = 0; index < field.count; index += 1) {
    const sm = field.positions[index];
    let intensity = 0.04;

    if (head > -0.5) {
      const band = Math.exp(-(((sm.seamDist - head) * 4.1) ** 2));
      intensity += band * 0.78;
    }
    intensity *= idle;

    const base = field.baseColors[index];
    scratch.copy(base).lerp(HOT, THREE.MathUtils.clamp(intensity, 0, 1) * 0.7);
    field.rings.setColorAt(index, ringTint(scratch.clone()).multiplyScalar(0.9 + intensity * 0.25));
    field.dots.setColorAt(index, scratch.clone().multiplyScalar(0.72 + intensity * 0.42));
  }

  if (field.rings.instanceColor) field.rings.instanceColor.needsUpdate = true;
  if (field.dots.instanceColor) field.dots.instanceColor.needsUpdate = true;
}

function updatePackets(packets, stage, elapsed, explode) {
  const { mesh, lanes, dummy, scratch } = packets;
  const reverse = stage.id === 'write';
  const activeRoute =
    stage.id === 'fetch' || stage.id === 'write'
      ? 'memory'
      : stage.id === 'd2d'
        ? 'd2d'
        : stage.id === 'nvlink'
          ? 'nvlink'
          : null;

  lanes.forEach((lane, index) => {
    const active = activeRoute === lane.route && explode < 0.2;
    if (!active) {
      dummy.position.set(0, 0, -50);
      dummy.scale.set(0.001, 0.001, 0.001);
      dummy.updateMatrix();
      mesh.setMatrixAt(index, dummy.matrix);
      return;
    }

    const travel = (stage.progress * 1.9 + lane.offset) % 1;
    const eased = travel;
    const from = reverse ? lane.to : lane.from;
    const to = reverse ? lane.from : lane.to;

    dummy.position.lerpVectors(from, to, eased);
    dummy.position.z += Math.sin(eased * Math.PI) * 0.06;

    const fade = Math.sin(Math.PI * THREE.MathUtils.clamp(eased, 0, 1)) ** 0.4;
    const stretch = 2 + fade * 3.4;
    if (lane.axis === 'y') dummy.scale.set(1.2, stretch, 1);
    else dummy.scale.set(stretch, 1.2, 1);
    dummy.updateMatrix();
    mesh.setMatrixAt(index, dummy.matrix);
    mesh.setColorAt(index, scratch.copy(lane.color).multiplyScalar(0.35 + fade * 1.9));
  });

  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
}
